import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { UpcomingList } from "@/components/messages/CheckinStrip";
import { CalendarFeed } from "@/components/messages/CalendarFeed";

export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const MODES = [["phone", "Phone call"], ["in_person", "In person"], ["video", "Video call"]];
const field = "px-3 py-2 rounded-xl bg-white border border-[#E6DEC3] text-sm focus:outline-none focus:border-[#2C4A3E]";

const WindowRow = ({ w, i, set, remove }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid={`hours-window-${i}`}>
    <select value={w.day} onChange={(e) => set({ ...w, day: Number(e.target.value) })} className={field} data-testid={`hours-day-${i}`}>
      {DAYS.map((d, n) => <option key={d} value={n}>{d}</option>)}</select>
    <input type="time" value={w.start} onChange={(e) => set({ ...w, start: e.target.value })} className={field} data-testid={`hours-start-${i}`} />
    <span className="text-stone-400 text-xs">to</span>
    <input type="time" value={w.end} onChange={(e) => set({ ...w, end: e.target.value })} className={field} data-testid={`hours-end-${i}`} />
    <select value={w.mode} onChange={(e) => set({ ...w, mode: e.target.value })} className={field} data-testid={`hours-mode-${i}`}>
      {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
    <button type="button" onClick={remove} aria-label="Remove" data-testid={`hours-remove-${i}`} className="p-2 text-stone-400 hover:text-[#8A3B2E]"><X className="w-4 h-4" /></button>
  </div>
);

const Blocked = ({ dates, setDates }) => {
  const [d, setD] = useState("");
  const add = () => { if (d && !dates.includes(d)) setDates([...dates, d].sort()); setD(""); };
  return (
    <div>
      <p className="text-sm font-medium text-[#2C4A3E] mb-2">Days off</p>
      <div className="flex gap-2"><input type="date" value={d} onChange={(e) => setD(e.target.value)} className={field} data-testid="hours-block-date" />
        <button type="button" onClick={add} data-testid="hours-block-add" className="px-4 rounded-full border border-[#E6DEC3] bg-white text-sm">Block day</button></div>
      <div className="flex flex-wrap gap-2 mt-2">{dates.map((x) => (
        <span key={x} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FBE9E7] text-xs text-[#8A3B2E]" data-testid={`hours-blocked-${x}`}>{x}
          <button type="button" onClick={() => setDates(dates.filter((y) => y !== x))} aria-label="Unblock"><X className="w-3 h-3" /></button></span>))}</div>
    </div>
  );
};

export const ConferenceHours = ({ open, setOpen }) => {
  const [h, setH] = useState(null);
  useEffect(() => { if (open) http.get("/conferences/hours").then((r) => setH(r.data)); }, [open]);
  const setW = (i, w) => setH({ ...h, windows: h.windows.map((x, n) => (n === i ? w : x)) });
  const save = async () => {
    try { await http.put("/conferences/hours", h); toast.success("Check-in hours saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FDFBF7] max-w-2xl max-h-[88vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="hours-dialog">
        <DialogTitle className="font-display text-2xl text-[#2C4A3E]">Parent check-in hours</DialogTitle>
        {h && (
          <div className="space-y-5">
            <p className="text-xs text-stone-500">Parents book open slots at least 24 hours ahead. Times are Eastern Time.</p>
            <label className="flex items-center gap-3 text-sm">Each check-in lasts
              <select value={h.duration} onChange={(e) => setH({ ...h, duration: Number(e.target.value) })} className={field} data-testid="hours-duration">
                {[15, 20, 30].map((m) => <option key={m} value={m}>{m} minutes</option>)}</select></label>
            <div className="space-y-2">
              <p className="text-sm font-medium text-[#2C4A3E]">Weekly open times</p>
              {h.windows.map((w, i) => <WindowRow key={i} w={w} i={i} set={(x) => setW(i, x)} remove={() => setH({ ...h, windows: h.windows.filter((_, n) => n !== i) })} />)}
              <button type="button" onClick={() => setH({ ...h, windows: [...h.windows, { day: 1, start: "15:00", end: "17:00", mode: "phone" }] })} data-testid="hours-add-window"
                className="inline-flex items-center gap-1.5 text-sm text-[#C87D53]"><Plus className="w-4 h-4" />Add a time block</button>
            </div>
            <Blocked dates={h.blocked_dates} setDates={(d) => setH({ ...h, blocked_dates: d })} />
            <label className="block text-sm font-medium text-[#2C4A3E]" htmlFor="hours-location">In-person location</label>
            <input id="hours-location" value={h.location || ""} onChange={(e) => setH({ ...h, location: e.target.value })} maxLength={200} placeholder="In-person location, e.g. Room 12, front office" className={`${field} w-full`} data-testid="hours-location" />
            <button onClick={save} data-testid="hours-save" className="w-full py-3 rounded-full bg-[#C87D53] text-white text-sm">Save hours</button>
            <CalendarFeed />
            <div><p className="text-sm font-medium text-[#2C4A3E] mb-2">Upcoming check-ins</p><UpcomingList /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ParentCheckins = ({ open, setOpen }) => (
  <Dialog open={open} onOpenChange={setOpen}>
    <DialogContent className="bg-[#FDFBF7] max-w-xl max-h-[88vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="parent-checkins-dialog">
      <DialogTitle className="font-display text-2xl text-[#2C4A3E]">My check-ins</DialogTitle>
      <p className="text-xs text-stone-500">Book a new check-in from your chat with a teacher.</p>
      <CalendarFeed />
      <UpcomingList />
    </DialogContent>
  </Dialog>
);
